import React, { useState } from 'react'
import { TextInput } from 'uiComponents/input'
import { showWarningMessage } from 'uiComponents/message'
import { Permission, UserPin } from 'settings/schema'
import { CopyIcon } from 'uiComponents/buttons/copyIcon'
import classNames from 'classnames'
import { delay } from 'utils'
import { PermissionGroup, PermissionGroupContent } from '../components/permissionGroup'
import { PermissionToggle } from '../components/permissionToggle'
import './pointOfSale.scss'

const PIN_LENGTH = 6

interface PointOfSalePermissionGroupProps {
    userId: number | null
    userPin: UserPin | null
    isPosLoginGranted: boolean
    posUserManagementEnabled: boolean
    isAdminGranted: boolean
    generateUserPin: (userId: number) => Promise<UserPin | null>
    regenerateUserPin: (pinId: number) => Promise<UserPin | null>
    handlePermissionClick: (permission: Permission, granted: boolean) => void
}

export const PointOfSalePermissionGroup = ({
    userId,
    userPin,
    isPosLoginGranted,
    posUserManagementEnabled,
    isAdminGranted,
    generateUserPin,
    regenerateUserPin,
    handlePermissionClick,
}: PointOfSalePermissionGroupProps) => {
    const [isGeneratingPin, setIsGeneratingPin] = useState(false)
    const [randomPin, setRandomPin] = useState('')

    const generateRandomPin = (length: number) => {
        let result = ''
        for (let i = 0; i < length; i++) {
            result += Math.floor(Math.random() * 10).toString()
        }
        return result
    }

    const revealPinGradually = async (pin: string) => {
        for (let i = 0; i <= PIN_LENGTH; i++) {
            const revealed = pin.substring(0, i)

            const stepStartTime = Date.now()
            while (Date.now() - stepStartTime < 100) {
                setRandomPin(revealed + generateRandomPin(PIN_LENGTH - i))
                await delay(25)
            }
        }
    }

    const handleGeneratePin = async () => {
        const minGeneratingTime = 300
        if (!userId) {
            showWarningMessage('Please save the user before generating a PIN code.')
            return
        }

        const startTime = Date.now()
        setIsGeneratingPin(true)

        const shuffleInterval = setInterval(() => {
            setRandomPin(generateRandomPin(PIN_LENGTH))
        }, 25)

        const newUserPin = await (userPin ? regenerateUserPin(userPin.id) : generateUserPin(userId))

        const endTime = Date.now()
        const generatingTime = endTime - startTime
        if (generatingTime < minGeneratingTime) {
            await delay(minGeneratingTime - generatingTime)
        }

        clearInterval(shuffleInterval)
        if (newUserPin) {
            await revealPinGradually(newUserPin.pin)
        }
        setIsGeneratingPin(false)
    }

    let pinValue = ''
    if (isGeneratingPin) {
        pinValue = randomPin
    } else if (userPin) {
        pinValue = userPin.pin || '******'
    }

    return (
        <PermissionGroup
            brandedTitle="Point of Sale"
            expanded={isPosLoginGranted && posUserManagementEnabled}
            className="pos-permissions"
        >
            <div className="row">
                <PermissionToggle
                    permission="pos_login"
                    disabled={isAdminGranted || !posUserManagementEnabled}
                    handleClick={handlePermissionClick}
                />
            </div>
            {!posUserManagementEnabled && (
                <div className="pos-management-required">Requires Convious Point of Sale terminals</div>
            )}
            <PermissionGroupContent height={78}>
                <div
                    className={classNames('pin-wrapper', {
                        'has-pin': userPin?.pin && !isGeneratingPin,
                        generating: isGeneratingPin,
                    })}
                >
                    <div className="pin-title">PIN code</div>
                    <div className="generate-pin" onClick={handleGeneratePin}>
                        Generate{userPin ? ' new' : ''}
                    </div>
                    <TextInput type="text" name="pin" disabled value={pinValue} placeholder="No PIN set" />
                    {userPin?.pin && !isGeneratingPin && <CopyIcon text={userPin.pin} />}
                    <div className="pin-explanation">
                        Please copy the PIN code and share it with the user. You will only be able to see it once.
                    </div>
                </div>
            </PermissionGroupContent>
        </PermissionGroup>
    )
}
